/**
 * GENERADOR DE TELEMETRIA SIMULADA - TEMPORAL.
 *
 * Sustituye a la telemetria real mientras el pipeline sigue caido y la tabla
 * `NissaGuard-Telemetry-dev` esta vacia.
 *
 * Dos propiedades que lo hacen util y no un simple `Math.random()`:
 *
 * 1. ES DETERMINISTA. La misma hora del mismo dispositivo devuelve siempre el
 *    mismo valor. Sin esto, cada render daria cifras distintas y el consumo del
 *    panel no cuadraria con el del historico ni con el agregado de la cuenta.
 *
 * 2. CADA DISPOSITIVO TIENE UN PERFIL PROPIO. Un edificio concurrido y una
 *    exposición no consumen igual ni a las mismas horas. Si ambos dieran curvas
 *    parecidas, cambiar de dispositivo no se notaria y no se podria comprobar
 *    que la interfaz reacciona.
 */

/** Hash estable de una cadena. Base del generador pseudoaleatorio. */
function hash(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Pseudoaleatorio determinista en [0,1) a partir de una semilla. */
function rand(seed) {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

/**
 * Perfiles de consumo. Cada dispositivo tiene una personalidad reconocible.
 *
 * `curve` devuelve el factor de consumo de cada hora (0 = nada, 1 = pico).
 * Es lo que hace que las graficas de un dispositivo y otro se vean distintas
 * de un vistazo, no solo con cifras diferentes.
 */
export const PROFILES = {
  'nissa-guard-001': {
    label: 'Edificio concurrido',
    litersPerDay: 6500,
    basePressure: 54,
    variance: 0.06,
    // Consumo continuo, mayor durante el horario de mayor afluencia.
    curve: (h) => {
      const jornada = Math.exp(-((h - 13) ** 2) / 38) * 0.45;
      const entrada = Math.exp(-((h - 8) ** 2) / 7) * 0.15;
      return 0.75 + jornada + entrada;
    },
  },
  'nissa-guard-002': {
    label: 'Exposición',
    litersPerDay: 4500,
    basePressure: 52,
    variance: 0.08,
    // Consumo continuo, con una subida moderada durante la exposición.
    curve: (h) => {
      const visitantes = Math.exp(-((h - 14) ** 2) / 35) * 0.35;
      return 0.68 + visitantes;
    },
  },
};

/** Perfil por defecto para dispositivos vinculados durante la demo. */
const DEFAULT_PROFILE = {
  label: 'Uso general',
  litersPerDay: 120,
  basePressure: 50,
  variance: 0.22,
  curve: (h) => Math.exp(-((h - 9) ** 2) / 8) + Math.exp(-((h - 19) ** 2) / 8) * 0.8 + 0.05,
};

export function profileOf(deviceId) {
  return PROFILES[deviceId] ?? DEFAULT_PROFILE;
}

/** Litros consumidos por un dispositivo en una hora concreta. */
export function litersAtHour(deviceId, date, hour) {
  const p = profileOf(deviceId);
  const dayKey = `${deviceId}|${date.getFullYear()}-${date.getMonth()}-${date.getDate()}|${hour}`;
  const ruido = (rand(hash(dayKey)) - 0.5) * 2 * p.variance;

  // Se normaliza la curva para que el total del día se acerque a litersPerDay.
  let suma = 0;
  for (let h = 0; h < 24; h++) suma += p.curve(h);
  const base = (p.curve(hour) / suma) * p.litersPerDay;

  return Math.max(0, +(base * (1 + ruido)).toFixed(2));
}

/** Estado de la válvula en un instante. Antes del primer cambio estaba abierta. */
export function valveOpenAt(events = [], timestamp = Date.now()) {
  let abierta = true;
  for (const event of events) {
    if (event.at > timestamp) break;
    abierta = event.state === 'open';
  }
  return abierta;
}

/** Litros integrados entre dos instantes usando el mismo perfil horario. */
export function litersBetween(deviceId, start, end, events = []) {
  let total = 0;
  for (let cursor = start; cursor < end;) {
    const hora = new Date(cursor);
    hora.setMinutes(0, 0, 0);
    const inicioHora = hora.getTime();
    hora.setHours(hora.getHours() + 1);
    const finHora = Math.min(hora.getTime(), end);
    const litrosHora = litersAtHour(deviceId, new Date(inicioHora), new Date(inicioHora).getHours());

    let tramo = cursor;
    let abierta = valveOpenAt(events, tramo);
    for (const event of events) {
      if (event.at <= tramo) continue;
      if (event.at >= finHora) break;
      if (abierta) total += litrosHora * (event.at - tramo) / (hora.getTime() - inicioHora);
      tramo = event.at;
      abierta = event.state === 'open';
    }
    if (abierta) total += litrosHora * (finHora - tramo) / (hora.getTime() - inicioHora);
    cursor = finHora;
  }
  return +total.toFixed(2);
}

/** Un día completo, o sólo hasta cutoff si es el día en curso. */
export function litersOfDay(deviceId, date, events = [], cutoff = null, since = 0) {
  const inicio = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const fin = Math.min(new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1).getTime(), cutoff?.getTime() ?? Infinity);
  let total = 0;
  for (let h = 0; h < 24; h++) {
    const desde = Math.max(inicio + h * 3600000, since);
    const hasta = Math.min(inicio + (h + 1) * 3600000, fin);
    if (hasta > desde) total += litersBetween(deviceId, desde, hasta, events);
  }
  return +total.toFixed(2);
}

/** Consumo del día en curso hasta el instante actual. */
export function litersToday(deviceId, now = new Date(), events = [], since = 0) {
  return litersOfDay(deviceId, now, events, now, since);
}

/**
 * Consumo de las ultimas 24 horas, no del dia natural.
 *
 * Es la ventana que usa la comparativa entre dispositivos. El motivo es
 * practico: a las 00:54 el "consumo de hoy" es de un litro y el panel parece
 * roto o vacio, aunque el dato sea correcto. Una ventana movil siempre tiene
 * contenido y ademas compara mejor: enfrenta ciclos completos de uso en lugar
 * de fracciones de dia distintas segun la hora a la que se mire.
 */
export function litersLast24h(deviceId, now = new Date(), events = [], since = 0) {
  return litersBetween(deviceId, Math.max(now.getTime() - 86400000, since), now.getTime(), events);
}

export function litersThisMonth(deviceId, now = new Date(), events = [], since = 0) {
  let total = 0;
  for (let d = 1; d <= now.getDate(); d++) {
    total += litersOfDay(deviceId, new Date(now.getFullYear(), now.getMonth(), d), events, now, since);
  }
  return +total.toFixed(2);
}

/** Caudal instantáneo en L/min, derivado del consumo de la hora en curso. */
export function flowRateNow(deviceId, now = new Date()) {
  return +(litersAtHour(deviceId, now, now.getHours()) / 60).toFixed(2);
}

/** Presión estimada: cae con el caudal y es 0 si la válvula está cerrada. */
export function pressureNow(deviceId, valveOpen = true, now = new Date()) {
  if (!valveOpen) return 0;
  const p = profileOf(deviceId);
  const caudal = flowRateNow(deviceId, now);
  const caida = Math.min(caudal * 1.6, 14);
  const ondulacion = (rand(hash(`${deviceId}|p|${Math.floor(now.getTime() / 9000)}`)) - 0.5) * 2.4;
  return +Math.max(0, p.basePressure - caida + ondulacion).toFixed(1);
}

/**
 * Serie de telemetría para un rango. Un punto cada 30 minutos: suficiente para
 * que las gráficas se vean densas sin generar miles de objetos en el navegador.
 */
export function telemetrySeries(deviceId, { hours = 24, events = [], since = null, now = new Date() } = {}) {
  const puntos = [];
  const ahora = now.getTime();
  const desde = since ?? new Date(now.getFullYear(), now.getMonth(), now.getDate() - Math.ceil(hours / 24)).getTime();
  const pasos = Math.min(Math.ceil(hours * 2), 2000);
  let acumulado = 0;
  let anterior = null;

  for (let i = pasos; i >= 0; i--) {
    const t = new Date(ahora - i * 30 * 60 * 1000);
    const abierta = valveOpenAt(events, t.getTime());
    const caudal = abierta ? flowRateNow(deviceId, t) : 0;
    if (anterior === null) acumulado = litersBetween(deviceId, desde, t.getTime(), events);
    else acumulado = +(acumulado + litersBetween(deviceId, anterior, t.getTime(), events)).toFixed(2);
    anterior = t.getTime();
    puntos.push({
      device_id: deviceId,
      timestamp: Math.floor(t.getTime() / 1000),
      flow_rate_lpm: caudal,
      flow_liters_total: acumulado,
      daily_consumption: litersToday(deviceId, t, events, desde),
      pressure_psi: pressureNow(deviceId, abierta, t),
      valve_state: abierta ? 'open' : 'closed',
      valve_open: abierta,
    });
  }
  return puntos;
}

/** Agregado por día, que es lo que consume la pantalla de historial. */
function aggregateDay(deviceId, d, events, now, since) {
  const inicio = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const horas = [];
  for (let h = 0; h < 24; h++) {
    const desde = Math.max(inicio + h * 3600000, since);
    const hasta = Math.min(inicio + (h + 1) * 3600000, now.getTime());
    horas.push({ hour: h, liters: hasta > desde ? litersBetween(deviceId, desde, hasta, events) : 0 });
  }
  const litros = +horas.reduce((total, h) => total + h.liters, 0).toFixed(2);
  return {
    date: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
    total_liters: litros,
    peak_flow_lpm: +Math.max(...horas.map((h) =>
      h.liters > 0 ? flowRateNow(deviceId, new Date(inicio + h.hour * 3600000)) : 0
    )).toFixed(2),
    hourly: horas,
  };
}

export function aggregatedByDay(deviceId, days = 7, events = [], now = new Date(), since = 0) {
  const salida = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
    if (new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).getTime() > since) {
      salida.push(aggregateDay(deviceId, d, events, now, since));
    }
  }
  return salida;
}

/** Mes o rango de fechas, con el mismo cálculo que usa el panel. */
export function aggregatedByDateRange(deviceId, startDate, endDate, events = [], now = new Date(), since = 0) {
  const salida = [];
  const [sy, sm, sd] = startDate.split('-').map(Number);
  const [ey, em, ed] = endDate.split('-').map(Number);
  const fin = new Date(ey, em - 1, ed);
  for (let d = new Date(sy, sm - 1, sd); d <= fin && d <= now; d.setDate(d.getDate() + 1)) {
    if (new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).getTime() > since) {
      salida.push(aggregateDay(deviceId, d, events, now, since));
    }
  }
  return salida;
}

/** Escenarios propios de los dos equipos de la cuenta simulada. */
const ALERT_SCENARIOS = {
  'nissa-guard-001': { name: 'NisaGuard Edificio 9', peakHour: 13 },
  'nissa-guard-002': { name: 'NisaGuard Exposicion', peakHour: 14 },
};

/** Día local en la clave estable de una alerta. */
function alertDateKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/**
 * Avisos basados en el mismo consumo que alimenta las gráficas.
 * Una válvula cerrada no puede producir avisos nuevos de flujo o caudal.
 */
export function recentAlerts(deviceId, days = 7, {
  events = [], since = 0, wifiRssi = null, now = new Date(),
} = {}) {
  const scenario = ALERT_SCENARIOS[deviceId];
  if (!scenario || days <= 0) return [];

  const alerts = [];
  const cutoff = now.getTime() - days * 86400000;
  const profile = profileOf(deviceId);

  // Una hora completa de mayor afluencia debe superar el promedio horario.
  for (let offset = 0; offset < Math.ceil(days); offset++) {
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - offset, scenario.peakHour);
    const end = new Date(start.getFullYear(), start.getMonth(), start.getDate(), scenario.peakHour + 1);
    if (start.getTime() < since || end.getTime() > now.getTime() || end.getTime() < cutoff) continue;
    const liters = litersBetween(deviceId, start.getTime(), end.getTime(), events);
    if (liters <= profile.litersPerDay / 24 * 1.1) continue;
    alerts.push({
      id: `${deviceId}-high-hourly-rate-${alertDateKey(start)}`,
      device_id: deviceId,
      type: 'high_hourly_rate',
      severity: 'warning',
      message: `${scenario.name} registró ${liters.toFixed(1)} L en una hora, por encima de su promedio habitual.`,
      recommendation: 'Revisa si el aumento coincide con la afluencia de personas.',
      timestamp: Math.floor(end.getTime() / 1000),
    });
    break;
  }

  // Un tramo nocturno con 20 minutos continuos de paso de agua.
  for (let offset = 0; offset < Math.ceil(days); offset++) {
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - offset, 2);
    const end = new Date(start.getTime() + 20 * 60000);
    if (start.getTime() < since || end.getTime() > now.getTime() || end.getTime() < cutoff) continue;
    const interrupted = events.some((event) =>
      event.at > start.getTime() && event.at < end.getTime() && event.state === 'closed');
    if (!valveOpenAt(events, start.getTime()) || interrupted) continue;
    const liters = litersBetween(deviceId, start.getTime(), end.getTime(), events);
    if (liters <= 0) continue;
    alerts.push({
      id: `${deviceId}-continuous-flow-${alertDateKey(start)}`,
      device_id: deviceId,
      type: 'continuous_flow',
      severity: 'warning',
      message: `${scenario.name} mantuvo flujo nocturno durante 20 minutos (${liters.toFixed(1)} L).`,
      recommendation: 'Comprueba si ese consumo corresponde al funcionamiento esperado del sitio.',
      timestamp: Math.floor(end.getTime() / 1000),
    });
    break;
  }

  if (Number.isFinite(Number(wifiRssi)) && Number(wifiRssi) <= -70) {
    const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 16);
    if (date.getTime() >= cutoff && date.getTime() >= since) {
      alerts.push({
        id: `${deviceId}-weak-wifi-signal-${alertDateKey(date)}`,
        device_id: deviceId,
        type: 'weak_wifi_signal',
        severity: 'warning',
        message: `${scenario.name} reportó una señal WiFi de ${wifiRssi} dBm, suficiente para seguir en línea pero con riesgo de interrupciones.`,
        recommendation: 'Revisa la distancia al punto de acceso o posibles obstáculos.',
        timestamp: Math.floor(date.getTime() / 1000),
      });
    }
  }

  return alerts.sort((a, b) => b.timestamp - a.timestamp);
}
